
import { useState, useEffect } from 'react'
import { FiX, FiDollarSign, FiAlertCircle, FiCheckCircle, FiInfo } from 'react-icons/fi'
import ButtonLoading from './ButtonLoading'
import SendOTP from './SendOTP'
import { formatCurrency } from '../utils/currencyUtils'
import { userRequest } from '../utils/requestMethods'

const TRANSACTION_FEE = 50;
const VAT_RATE = 0.075;
const VAT = TRANSACTION_FEE * VAT_RATE; // 3.75

const WithdrawModal = ({ closeModal, school, amount, setAmount, setRecipientCode, otp, setOtp, bankDetails, balance, submitHandler, loading, error, success, onFundSelect }) => {
  const multiFundEnabled = !!school?.settings?.finance?.multi_fund_enabled;
  const [funds, setFunds] = useState([]);
  const [selectedFundId, setSelectedFundId] = useState('');

  useEffect(() => {
    if (!school?._id || !multiFundEnabled) return;
    const fetchFunds = async () => {
      try {
        const { data } = await userRequest.get(`/schools/${school._id}/funds`);
        const list = data?.data?.funds || data?.funds || [];
        if (Array.isArray(list) && list.length > 0) {
          setFunds(list);
          const defaultFund = list.find((f) => f.is_default) || list[0];
          const initialFundId = defaultFund?._id || '';
          setSelectedFundId(initialFundId);
          onFundSelect?.(initialFundId);
        }
      } catch (err) {
        console.error(err);
      }
    };
    fetchFunds();
  }, [school?._id, multiFundEnabled]);

  const selectedFund = funds.find((f) => f._id === selectedFundId);
  const activeAvailableBalance = multiFundEnabled && selectedFund?.balance
    ? parseFloat(selectedFund.balance.availableBalance) || 0
    : parseFloat(balance?.availableBalance) || 0;

  const amountNum = parseFloat(amount) || 0;
  const totalDeduction = amountNum + TRANSACTION_FEE + VAT;
  const availableBalance = activeAvailableBalance;
  const hasSufficientBalance = amountNum > 0 && availableBalance >= totalDeduction;
  const showBreakdown = amountNum > 0;

  return (
    <div>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
        onClick={closeModal}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">

          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2">
              <div className="p-2 bg-indigo-50 rounded-xl">
                <FiDollarSign className="text-indigo-600 text-lg" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Withdraw Funds</h3>
            </div>
            <button
              type="button"
              onClick={closeModal}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FiX className="w-5 h-5" />
            </button>
          </div>

          {/* Body */}
          <div className="px-6 py-5 space-y-5">

            {/* Destination account info */}
            {bankDetails?.account_name && (
              <div className="rounded-xl bg-gray-50 border border-gray-200 px-4 py-3">
                <p className="text-xs text-gray-500 mb-1">Withdrawing to</p>
                <p className="text-sm font-semibold text-gray-800">{bankDetails.account_name}</p>
                <p className="text-sm text-gray-600">{bankDetails.account_number}</p>
                <p className="text-xs text-gray-400 mt-0.5">{bankDetails.bank_name}</p>
              </div>
            )}

            {/* Alerts */}
            {error && (
              <div className="flex items-start gap-3 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
                <FiAlertCircle className="mt-0.5 shrink-0" />
                <span>{typeof error === 'string' ? error : 'An error occurred. Please try again.'}</span>
              </div>
            )}
            {success && (
              <div className="flex items-start gap-3 bg-green-50 border border-green-200 text-green-700 rounded-xl px-4 py-3 text-sm">
                <FiCheckCircle className="mt-0.5 shrink-0" />
                <span><b>Success!</b> Withdrawal processed.</span>
              </div>
            )}

            <form onSubmit={submitHandler} className="space-y-4">
              <SendOTP school={school} />

              {multiFundEnabled && funds.length > 0 && (
                <div>
                  <label className="block mb-1.5 text-sm font-medium text-gray-700">
                    Source Fund <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={selectedFundId}
                    onChange={(e) => {
                      setSelectedFundId(e.target.value);
                      onFundSelect?.(e.target.value);
                    }}
                    className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
                  >
                    {funds.map((f) => (
                      <option key={f._id} value={f._id}>
                        {f.name} {f.is_default ? "(Default)" : ""} — ₦
                        {parseFloat(f.balance?.availableBalance || 0).toLocaleString()}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div>
                <label className="block mb-1.5 text-sm font-medium text-gray-700">Amount (₦)</label>
                <input
                  type="number"
                  onChange={(e) => setAmount(e.target.value)}
                  value={amount}
                  placeholder="e.g. 5000"
                  className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
                />
              </div>

              {/* Fee breakdown */}
              {showBreakdown && (
                <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 space-y-2 text-sm">
                  <div className="flex justify-between text-gray-600">
                    <span>Withdrawal amount</span>
                    <span>{formatCurrency(amountNum)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span className="flex items-center gap-1">
                      Transaction fee
                      <FiInfo className="text-gray-400 text-xs" title="Flat fee per withdrawal" />
                    </span>
                    <span>{formatCurrency(TRANSACTION_FEE)}</span>
                  </div>
                  <div className="flex justify-between text-gray-600">
                    <span className="flex items-center gap-1">
                      VAT (7.5%)
                      <FiInfo className="text-gray-400 text-xs" title="7.5% VAT on transaction fee" />
                    </span>
                    <span>{formatCurrency(VAT)}</span>
                  </div>
                  <div className="border-t border-gray-200 pt-2 flex justify-between font-semibold text-gray-800">
                    <span>Total deducted</span>
                    <span>{formatCurrency(totalDeduction)}</span>
                  </div>
                  <div className={`flex justify-between text-xs pt-1 ${hasSufficientBalance ? 'text-green-600' : 'text-red-500'}`}>
                    <span>Available balance</span>
                    <span>{formatCurrency(availableBalance)}</span>
                  </div>
                  {!hasSufficientBalance && (
                    <div className="flex items-center gap-1.5 text-xs text-red-500 pt-1">
                      <FiAlertCircle className="shrink-0" />
                      Insufficient balance for this withdrawal
                    </div>
                  )}
                </div>
              )}

              {/* Hidden radio — keep for submit logic */}
              <input
                type="radio"
                className="hidden"
                checked
                readOnly
                value={bankDetails?.recipient_code}
                onChange={(e) => setRecipientCode(e.target.value)}
              />

              <div>
                <label className="block mb-1.5 text-sm font-medium text-gray-700">OTP</label>
                <input
                  type="text"
                  onChange={(e) => setOtp(e.target.value)}
                  value={otp}
                  placeholder="Enter generated OTP"
                  className="w-full rounded-xl bg-gray-50 border border-gray-200 px-4 py-2.5 text-sm text-gray-900 focus:border-indigo-500 focus:ring-indigo-500 focus:outline-none"
                />
              </div>

              {loading ? (
                <ButtonLoading styles="w-full text-white bg-indigo-500 focus:outline-none font-medium rounded-full text-sm px-5 py-2.5 text-center" />
              ) : (
                <button
                  type="submit"
                  disabled={showBreakdown && !hasSufficientBalance}
                  className="w-full text-white bg-indigo-600 hover:bg-indigo-700 focus:ring-4 focus:outline-none focus:ring-indigo-300 font-medium rounded-full text-sm px-5 py-2.5 text-center transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Confirm Withdrawal
                </button>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default WithdrawModal
